import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from "@ngx-translate/core";
import {
  StudentTicketListViewAsmResponseDTO
} from "../../../models/student-ticket/list-view/StudentTicketListViewAsmResponseDTO";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {TerminationFormModalComponent} from "../termination/termination-form-modal/termination-form-modal.component";
import {CustomToastService} from "../../../@core/utils/custom-toast.service";
import {StudentTicketService} from "../../../@core/services/student-ticket/student-ticket.service";
import {getTicketStatusColor} from "../../../models/student-ticket/TicketStatusASMColorMapping";
import {StudentTicketPaymentStatus} from "../../../models/student-ticket/payment/StudentTicketPaymentStatus";
import {
  getTicketPaymentStatusColor
} from "../../../models/student-ticket/payment/StudentTicketPaymentStatusColorMapping";
import {TicketStatusASM} from "../../../models/student-ticket/TicketStatusASM";
import {TicketTerminationStatusASM} from "../../../models/student-ticket/termination/TicketTerminationStatusASM";
import {
  getTicketTerminationStatusColor
} from "../../../models/student-ticket/termination/TicketTerminationStatusASMColorMapping";
import {TerminationRequestDTO} from "../../../models/student-ticket/termination/TerminationRequestDTO";
import {MandateHolderModalComponent} from "../mandate-holder-modal/mandate-holder-modal.component";
import {StudentTicketPaymentService} from "../../../@core/services/student-ticket/StudentTicketPaymentService";
import {
  StudentTicketChangeListService
} from "../../../@core/services/student-ticket/student-ticket-change-list.service";
import {StudentTicketChangeType} from "../../../models/student-ticket/change/StudentTicketChangeType";
import {ConfirmComponent} from "../../../shared/confirm/confirm.component";
import {
  AddressDetailsAdjustModalComponent
} from "../address-details-adjust-modal/address-details-adjust-modal.component";
import {AddressDataInfoDto} from "../../../models/student-ticket/list-view/AddressDataInfoDto";
import {SchoolDetailsAdjustModalComponent} from "../school-details-adjust-modal/school-details-adjust-modal.component";
import {SchoolDataInfoDto} from "../../../models/student-ticket/list-view/SchoolDataInfoDto";
import {NameDataInfoDto} from "../../../models/student-ticket/list-view/NameDataInfoDto";
import {NameDetailsAdjustModalComponent} from "../name-details-adjust-modal/name-details-adjust-modal.component";
import {GeneralConfigService} from "../../../@core/services/config/general-config/general-config.service";
import {GeschwisterAdjustModalComponent} from "../geschwister-adjust-modal/geschwister-adjust-modal.component";
import {EmailDetailsAdjustModalComponent} from "../email-details-adjust-modal/email-details-adjust-modal.component";
import {EmailDataInfoDto} from "../../../models/student-ticket/list-view/EmailDataInfoDto";
import {VerificationWizardComponent} from "../verification-wizard/verification-wizard.component";

@Component({
    selector: 'app-ticket-card',
    templateUrl: './student-ticket-card.component.html',
    styleUrl: './student-ticket-card.component.scss',
    standalone: false
})
export class StudentTicketCardComponent implements OnInit {

  @Input() ticket : StudentTicketListViewAsmResponseDTO;
  @Output() onCardContentChanged = new EventEmitter();

  protected accordionTicketTitle: string;
  protected accordionStatusTitle: string;
  protected accordionZahlungTitle: string;
  protected accordionUeberpruefungTitle: string;
  protected accordionAnhaengeTitle: string;
  protected lastVerifiedAt: Date | null = null;
  protected uniqueId: string;

  addressDataChangeEnabled = false;
  schoolDataChangeEnabled = false;
  geschwisterEigenanteilKomponenteEnabled = true;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public translateService: TranslateService,
    public studentTicketService: StudentTicketService,
    private readonly modalService: NgbModal,
    private readonly customToastService: CustomToastService,
    private readonly studentTicketPaymentService: StudentTicketPaymentService,
    private readonly studentTicketChangeListService: StudentTicketChangeListService,
    private readonly generalConfigService: GeneralConfigService,
  ) {}

  ngOnInit(): void {
    this.uniqueId = `ticket-${this.ticket?.id ?? crypto?.randomUUID()}`;
    if (this.ticket?.verifiedAt) {
      this.lastVerifiedAt = new Date(this.ticket.verifiedAt);
    }
    this.makeInit().then(()=>{});
    this.generalConfigService.getFeaturesConfig().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (config) => {
        this.addressDataChangeEnabled = config.addressDataChangeEnabled;
        this.schoolDataChangeEnabled = config.schoolDataChangeEnabled;
      }
    });
    this.generalConfigService.getEigenanteilConfig().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (conf) => {
        this.geschwisterEigenanteilKomponenteEnabled = conf?.geschwisterEigenanteilKomponenteEnabled ?? true;
      },
      error: () => {
        this.geschwisterEigenanteilKomponenteEnabled = true;
      }
    });
  }

  makeInit(): Promise<void>{
    return new Promise<void>((resolve)=>{

      //translations
      this.translateService.get(['TICKETS.TERMINATION_STATUS.STATUS_TITLE', 'TICKETS.TICKET', 'TICKETS.ZAHLUNG_TITLE', 'TICKETS.UEBERPRUEFUNG_TITLE', 'TICKETS.ANHAENGE_TITLE']).subscribe(translations => {
        this.accordionTicketTitle = translations['TICKETS.TICKET'];
        this.accordionStatusTitle = translations['TICKETS.TERMINATION_STATUS.STATUS_TITLE'];
        this.accordionZahlungTitle = translations['TICKETS.ZAHLUNG_TITLE'];
        this.accordionUeberpruefungTitle = translations['TICKETS.UEBERPRUEFUNG_TITLE'];
        this.accordionAnhaengeTitle = translations['TICKETS.ANHAENGE_TITLE'];
      });
        resolve();
    });
  }


  terminateTicket() {

    const modalRef = this.modalService.open(TerminationFormModalComponent, { size: 'lg' });
    modalRef.componentInstance.ticketId = this.ticket?.id;

    modalRef.result.then((terminationRequest: TerminationRequestDTO) => {
      if (terminationRequest) {
        this.studentTicketService.planTermination(this.ticket?.id, terminationRequest)
          .subscribe({
            next: () => {
              this.customToastService.showSuccess(this.translateService.instant("TERMINATE_TICKET.ERRORS.SUCCESS_SUBMISSION"));
              this.onCardContentChanged.emit(null);
            },
            error: (err) => {
              switch (err?.error?.reason) {
                case "307":
                case "308":
                case "312":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.REASON_WRONG_STATE"));
                  break;
                case "309":
                case "363":
                case "365":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.REASON_USER"));
                  break;
                case "343":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.REASON_TICKET_NOT_ACTIVE"));
                  break;
                case "320":
                case "364":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.REASON_TICKET_NOT_FOUND"));
                  break;
                case "366":
                case "367":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.VALIDATION_FAILED_DATE"));
                  break;
                case "368":
                case "369":
                case "370":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.VALIDATION_FAILED_TERMINATION_REASON"));
                  break;
                case "399":
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED") + " " + this.translateService.instant("TERMINATE_TICKET.ERRORS.TERMINATION_DATE_AFTER_EXPIRATION"));
                  break;
                default:
                  this.customToastService.showError(this.translateService.instant("TERMINATE_TICKET.ERRORS.ERROR_SUBMISSION_FAILED"));
              }
            }
          });
      }
    });
  }

  public onaddressDetailsAdjust(): void {
    const modalRef = this.modalService.open(AddressDetailsAdjustModalComponent, {size: 'lg'});
    modalRef.componentInstance.addressDetails = {
      street: this.ticket.addressData?.street,
      city: this.ticket.addressData?.city,
      postalCode: this.ticket.addressData?.postalCode,
      country: this.ticket.addressData?.country,
      addressAddition: this.ticket.addressData?.addressAddition,
    }

    modalRef.result.then((result) => {
      if (result) {
        this.studentTicketChangeListService.hasTicketActiveChangeForType(this.ticket.id, StudentTicketChangeType.ADDRESS_DATA)
          .subscribe({
            next: (activeChangeDto) => {
              if (activeChangeDto.hasActiveChange) {
                const modalRefActiveChange = this.modalService.open(ConfirmComponent, {size: 'md'});
                modalRefActiveChange.componentInstance.title = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TITLE');
                modalRefActiveChange.componentInstance.text = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TEXT');
                modalRefActiveChange.componentInstance.yesbuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.YES_BUTTON');
                modalRefActiveChange.componentInstance.nobuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.NO_BUTTON');
                modalRefActiveChange.componentInstance.onYes = () => {
                  this.updateAddressDetails(result);
                };
              } else {
                this.updateAddressDetails(result);
              }
            },
            error: err => {
              this.customToastService.showError(this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_CHECK_ERROR', { 'errorCode': err?.error?.reason }));
            }
          })
      }
    })
  }

  public onSchoolDetailsAdjust(): void {
    const modalRef = this.modalService.open(SchoolDetailsAdjustModalComponent, {size: 'lg'});
    modalRef.componentInstance.schoolDataInfo = {
      schuleId: this.ticket.schuleId,
      schulTyp: this.ticket.schuleTyp,
      schuljahrId: this.ticket.schuljahrId,
      klasseId: this.ticket.klasseId,
      bildungsgangId: this.ticket.bildungsgangId,
      schuleName: this.ticket.schuleName,
      schuljahrName: this.ticket.schuljahrName,
      klasseName: this.ticket.klasseName,
      bildungsgangName: this.ticket.bildungsgangName,
    }

    modalRef.result.then((result: SchoolDataInfoDto) => {
      if (result) {

        this.studentTicketChangeListService.hasTicketActiveChangeForType(this.ticket.id, StudentTicketChangeType.SCHOOL_DATA)
          .subscribe({
            next: (activeChangeDto) => {
              if (activeChangeDto.hasActiveChange) {
                const modalRefActiveChange = this.modalService.open(ConfirmComponent, {size: 'md'});
                modalRefActiveChange.componentInstance.title = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TITLE');
                modalRefActiveChange.componentInstance.text = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TEXT');
                modalRefActiveChange.componentInstance.yesbuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.YES_BUTTON');
                modalRefActiveChange.componentInstance.nobuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.NO_BUTTON');
                modalRefActiveChange.componentInstance.onYes = () => {
                  this.updateSchoolDetails(result);
                };
              } else {
                this.updateSchoolDetails(result);
              }
            },
            error: err => {
              this.customToastService.showError(this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_CHECK_ERROR', { 'errorCode': err?.error?.reason }));
            }
          })
      }
    })
  }

  onNameDetailsAdjust() {
    const modalRef = this.modalService.open(NameDetailsAdjustModalComponent, {size: 'lg'});
    modalRef.componentInstance.nameDetails = {
      firstName: this.ticket.firstname,
      lastName: this.ticket.lastname
    }

    modalRef.result.then((result: NameDataInfoDto) => {
      if (result) {

        this.studentTicketChangeListService.hasTicketActiveChangeForType(this.ticket.id, StudentTicketChangeType.NAME_DATA)
          .subscribe({
            next: (activeChangeDto) => {
              if (activeChangeDto.hasActiveChange) {
                const modalRefActiveChange = this.modalService.open(ConfirmComponent, {size: 'md'});
                modalRefActiveChange.componentInstance.title = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TITLE');
                modalRefActiveChange.componentInstance.text = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TEXT');
                modalRefActiveChange.componentInstance.yesbuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.YES_BUTTON');
                modalRefActiveChange.componentInstance.nobuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.NO_BUTTON');
                modalRefActiveChange.componentInstance.onYes = () => {
                  this.updateNameDetails(result);
                };
              } else {
                this.updateNameDetails(result);
              }
            },
            error: err => {
              this.customToastService.showError(this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_CHECK_ERROR', { 'errorCode': err?.error?.reason }));
            }
          })
      }
    })
  }

  private updateNameDetails(result: NameDataInfoDto) {
    this.studentTicketService.adjustNameData(this.ticket?.id, result)
      .subscribe({
        next: () => {
          this.ticket.firstname = result.firstName;
          this.ticket.lastname = result.lastName;
          this.customToastService.showSuccess(this.translateService.instant("NAME_DATA.SUCCESS"));
          this.customToastService.showInfo(this.translateService.instant('TICKETS.UEBERPRUEFUNG_INFO_NACH_BESTAETIGUNG'));
        },
        error: (err) => {
          switch (err?.error?.reason) {
            case "451":
              this.customToastService.showError(this.translateService.instant("NAME_DATA.ERRORS.TICKET_NOT_FOUND", { 'errorCode': err?.error?.reason}));
              break;
            case "452":
              this.customToastService.showError(this.translateService.instant("NAME_DATA.ERRORS.TICKET_INACTIVE", { 'errorCode': err?.error?.reason}));
              break;
            case "453":
              this.customToastService.showError(this.translateService.instant("NAME_DATA.ERRORS.DATA_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "454":
              this.customToastService.showError(this.translateService.instant("NAME_DATA.ERRORS.VALIDATION_FAILED_NAME_FIRSTNAME_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "455":
              this.customToastService.showError(this.translateService.instant("NAME_DATA.ERRORS.VALIDATION_FAILED_NAME_LASTNAME_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            default:
              this.customToastService.showError(this.translateService.instant("NAME_DATA.ERRORS.GENERAL", { 'errorCode': err?.error?.reason}));
              break;
          }
        }
      })
  }

  onEmailDetailsAdjust() {
    const modalRef = this.modalService.open(EmailDetailsAdjustModalComponent, {size: 'lg'});
    modalRef.componentInstance.emailDetails = {
      email: this.ticket.contactData?.email ?? ''
    };

    modalRef.result.then((result: EmailDataInfoDto) => {
      if (result) {
        this.studentTicketService.adjustEmailData(this.ticket.id, result)
          .subscribe({
            next: () => {
              if (!this.ticket.contactData) {
                this.ticket.contactData = {};
              }
              this.ticket.contactData.email = result.email;
              this.customToastService.showSuccess(this.translateService.instant('EMAIL_DATA.SUCCESS'));
            },
            error: (err) => {
              switch (err?.error?.reason) {
                case '424':
                  this.customToastService.showError(this.translateService.instant('EMAIL_DATA.ERRORS.TICKET_NOT_FOUND', {'errorCode': err?.error?.reason}));
                  break;
                case '425':
                  this.customToastService.showError(this.translateService.instant('EMAIL_DATA.ERRORS.TICKET_INACTIVE', {'errorCode': err?.error?.reason}));
                  break;
                case '426':
                  this.customToastService.showError(this.translateService.instant('EMAIL_DATA.ERRORS.EMAIL_MISSING', {'errorCode': err?.error?.reason}));
                  break;
                case '427':
                  this.customToastService.showError(this.translateService.instant('EMAIL_DATA.ERRORS.EMAIL_INVALID', {'errorCode': err?.error?.reason}));
                  break;
                default:
                  this.customToastService.showError(this.translateService.instant('EMAIL_DATA.ERRORS.GENERAL', {'errorCode': err?.error?.reason}));
                  break;
              }
            }
          });
      }
    });
  }

  cancelTicketTermination() {    this.studentTicketService.cancelTermination(this.ticket.id, this.ticket.terminationId)
      .subscribe({
        next: () => {
          this.customToastService.showSuccess("Kündigung zurückgezogen");
          this.onCardContentChanged.emit(null);
        },
        error: () => {
          this.customToastService.showError("Kündigung konnte nicht zurückgezogen werden");
        }
      });
  }

  getTicketStatusColor(status: TicketStatusASM): string {
    return getTicketStatusColor(status);
  }

  getTerminationStatusColor(status: TicketTerminationStatusASM): string {
    return getTicketTerminationStatusColor(status);
  }

  getPaymentStatusColor(status: StudentTicketPaymentStatus): string {
    return getTicketPaymentStatusColor(status);
  }

  getVerifiedStatusColor(ticketVerified): string {
    if(ticketVerified) {
      return 'var(--traffic-light-green, $traffic-light-green)';
    }
    else {
      return 'var(--traffic-light-red, $traffic-light-red)';
    }
  }

  onMandateInformationChange(){
    const modalRef = this.modalService.open(MandateHolderModalComponent, {size: 'lg'});
    modalRef.componentInstance.initialValues = this.ticket.mandateHolder; // @Input initialValues setzen
    modalRef.result.then(result => {
      if(result){
        this.studentTicketChangeListService.hasTicketActiveChangeForType(this.ticket.id, StudentTicketChangeType.MANDATE_HOLDER_DATA)
          .subscribe({
            next: (activeChangeDto) => {
              if (activeChangeDto.hasActiveChange) {
                const modalRefActiveChange = this.modalService.open(ConfirmComponent, {size: 'md'});
                modalRefActiveChange.componentInstance.title = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TITLE');
                modalRefActiveChange.componentInstance.text = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.TEXT');
                modalRefActiveChange.componentInstance.yesbuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.YES_BUTTON');
                modalRefActiveChange.componentInstance.nobuttontext = this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_MODAL.NO_BUTTON');
                modalRefActiveChange.componentInstance.onYes= () => {
                  this.updatePaymentInformation(result);
                };
              } else {
                this.updatePaymentInformation(result);
              }
            },
            error: err => {
              this.customToastService.showError(this.translateService.instant('TICKETS.CHANGE_LIST.ACTIVE_CHANGE_CHECK_ERROR', { 'errorCode': err?.error?.reason }));
            }
          })
      }
    })
  }

  updatePaymentInformation(result: any) {
    this.studentTicketPaymentService.updatePaymentInformation(this.ticket?.id, result)
      .subscribe({
        next: () => {
          this.ticket.mandateHolder = result;
          this.customToastService.showSuccess(this.translateService.instant('TICKETS.MANDATE_HOLDER_INFORMATION.SUCCESS'))
        }
      })
  }

  onGeschwisterAdjust(): void {
    const modalRef = this.modalService.open(GeschwisterAdjustModalComponent, {size: 'lg'});
    modalRef.componentInstance.ticketId = this.ticket.id;

    modalRef.result.then((result) => {
      if (result) {
        this.customToastService.showSuccess(
          this.translateService.instant('TICKETS.GESCHWISTER.ADJUST_SUCCESS')
        );
        this.customToastService.showInfo(this.translateService.instant('TICKETS.UEBERPRUEFUNG_INFO_NACH_BESTAETIGUNG'));
        this.onCardContentChanged.emit(null);
      }
    }).catch(() => {
      // Modal dismissed — no action needed
    });
  }

  updateAddressDetails(result: AddressDataInfoDto) {
    this.studentTicketService.adjustAddressData(this.ticket?.id, result)
      .subscribe({
        next: () => {
          this.ticket.addressData = result;
          this.customToastService.showSuccess(this.translateService.instant("ADDRESS_DATA.SUCCESS"));
          this.customToastService.showInfo(this.translateService.instant('TICKETS.UEBERPRUEFUNG_INFO_NACH_BESTAETIGUNG'));
        },
        error: (err) => {
          switch (err?.error?.reason) {
            case "420":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.TICKET_NOT_FOUND", { 'errorCode': err?.error?.reason}));
              break;
            case "421":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.TICKET_INACTIVE", { 'errorCode': err?.error?.reason}));
              break;
            case "422":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.DATA_ALREADY_TRANSMITTED", { 'errorCode': err?.error?.reason}));
              break;
            case "423":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.DATA_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "424":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.VALIDATION_FAILED_ADDRESS_STREET_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "425":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.VALIDATION_FAILED_ADDRESS_CITY_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "426":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.VALIDATION_FAILED_ADDRESS_POSTALCODE_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "427":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.VALIDATION_FAILED_ADDRESS_COUNTRY_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "428":
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.VALIDATION_FAILED_ADDRESS_POSTALCODE_INVALID", { 'errorCode': err?.error?.reason}));
              break;
            default:
              this.customToastService.showError(this.translateService.instant("ADDRESS_DATA.ERRORS.GENERAL", { 'errorCode': err?.error?.reason}));
              break;
          }
        }
      })
  }

  updateSchoolDetails(result: SchoolDataInfoDto) {
    this.studentTicketService.adjustSchoolData(this.ticket?.id, result)
      .subscribe({
        next: () => {
          this.applySchoolDataToTicket(result);
          this.customToastService.showSuccess(this.translateService.instant("SCHOOL_DATA.SUCCESS"));
          this.customToastService.showInfo(this.translateService.instant('TICKETS.UEBERPRUEFUNG_INFO_NACH_BESTAETIGUNG'));
        },
        error: (err) => {
          switch (err?.error?.reason) {
            case "444":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.TICKET_NOT_FOUND", { 'errorCode': err?.error?.reason}));
              break;
            case "445":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.TICKET_INACTIVE", { 'errorCode': err?.error?.reason}));
              break;
            case "446":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.DATA_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "447":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.VALIDATION_FAILED_SCHOOL_SCHOOLID_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "448":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.VALIDATION_FAILED_SCHOOL_SCHOOLYEAR_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "449":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.VALIDATION_FAILED_SCHOOL_CLASSID_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            case "450":
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.VALIDATION_FAILED_SCHOOL_COURSEID_MISSING", { 'errorCode': err?.error?.reason}));
              break;
            default:
              this.customToastService.showError(this.translateService.instant("SCHOOL_DATA.ERRORS.GENERAL", { 'errorCode': err?.error?.reason}));
              break;
          }
        }
      })
  }

  private applySchoolDataToTicket(result: SchoolDataInfoDto): void {
    this.ticket.schuleId = result.schuleId;
    this.ticket.schuljahrId = result.schuljahrId;
    this.ticket.klasseId = result.klasseId;
    this.ticket.bildungsgangId = result.bildungsgangId;
    this.ticket.schuleTyp = result.schulTyp;
    this.ticket.schuleName = result.schuleName;
    this.ticket.schuljahrName = result.schuljahrName;
    this.ticket.klasseName = result.klasseName;
    this.ticket.bildungsgangName = result.bildungsgangName;
  }

  verifyTicket(): void {
    if (!this.ticket?.id) {
      this.customToastService.showError(this.translateService.instant('TICKETS.UEBERPRUEFUNG_ERROR'));
      return;
    }
    this.studentTicketService.verifyTicket(this.ticket.id)
      .subscribe({
        next: (serverTimestamp) => {
          this.lastVerifiedAt = new Date(serverTimestamp);
        },
        error: () => {
          this.customToastService.showError(this.translateService.instant('TICKETS.UEBERPRUEFUNG_ERROR'));
        }
      });
  }

  openVerificationWizard(): void {
    if (!this.ticket?.id) {
      this.customToastService.showError(this.translateService.instant('TICKETS.UEBERPRUEFUNG_ERROR'));
      return;
    }
    const modalRef = this.modalService.open(VerificationWizardComponent, {size: 'lg', backdrop: 'static'});
    modalRef.componentInstance.ticket = this.ticket;
    modalRef.componentInstance.addressDataChangeEnabled = this.addressDataChangeEnabled;
    modalRef.componentInstance.schoolDataChangeEnabled = this.schoolDataChangeEnabled;
    modalRef.componentInstance.geschwisterEigenanteilKomponenteEnabled = this.geschwisterEigenanteilKomponenteEnabled;

    modalRef.result.then((verifiedAt: string | null) => {
      if (verifiedAt) {
        this.lastVerifiedAt = new Date(verifiedAt);
      }
    }).catch(() => {
      // Modal abgebrochen — keine Aktion nötig
    });
  }
}
